/**
 * How the SSE builder reads a stream (card b9799baf).
 *
 * It used the webview's `fetch`, which the desktop CSP's `connect-src` blocks for any
 * host that is not Apiboo's own: on desktop every SSE tab failed and retried forever.
 * On desktop the stream now goes through the SAME Tauri adapter as an HTTP request
 * (`sendRequest({ stream })` → `src-tauri/src/http_stream.rs`), with the user's network
 * settings (proxy, TLS, certificates). The web build keeps `fetch`, which works there.
 *
 * One callback shape for both:
 *   onOpen({ status, statusText, contentType })  the head arrived, the body follows
 *   onText(text)                                 a decoded piece of the body
 *   onEnd()                                      the server ended the stream
 *   onError(error)                               it failed or broke (not after an abort)
 *
 * @returns {() => void} abort — closes the connection; no callback fires after it
 */
import { isTauri } from './isTauri';
import { sendRequest } from './tauri';
import { cancelHttpStream } from './httpStreamTransport';
import { withUserNetwork } from './requestNetworkOptions';
import { getResponseRawText } from './responseBody';

let counter = 0;

const headerOf = (headers, name) => {
  const hit = Object.entries(headers || {}).find(([k]) => k.toLowerCase() === name);
  return hit ? String(hit[1]) : '';
};

function openNative(request, handlers) {
  const id = `sse-${Date.now()}-${++counter}`;
  let aborted = false;
  let opened = false;
  const early = []; // body pieces that beat the request's own answer
  const live = (fn) => (...args) => { if (!aborted) fn(...args); };

  const stream = {
    id,
    onOpen: () => !aborted,
    onChunk: live((text) => (opened ? handlers.onText(text) : early.push(() => handlers.onText(text)))),
    onDone: live(() => (opened ? handlers.onEnd() : early.push(handlers.onEnd))),
    onError: live((msg) => {
      const error = new Error(String(msg || 'The connection broke'));
      return opened ? handlers.onError(error) : early.push(() => handlers.onError(error));
    }),
    onNotAStream: () => {},
  };

  sendRequest(withUserNetwork({ ...request, stream }))
    .then(live((response) => {
      const status = response.status;
      const statusText = response.status_text || response.statusText || '';
      if (status < 200 || status >= 300) {
        const text = getResponseRawText(response);
        handlers.onError(new Error(`HTTP ${status}: ${text || statusText}`));
        return;
      }
      opened = true;
      handlers.onOpen({ status, statusText, contentType: headerOf(response.headers, 'content-type') });
      if (response.streaming) {
        early.splice(0).forEach((step) => step());
        return;
      }
      // Not `text/event-stream`: the whole body is already here — read it as one piece.
      const text = getResponseRawText(response);
      if (text) handlers.onText(text);
      handlers.onEnd();
    }))
    .catch(live((error) => handlers.onError(error instanceof Error ? error : new Error(String(error)))));

  return () => {
    if (aborted) return;
    aborted = true;
    cancelHttpStream(id);
  };
}

function openFetch(request, handlers) {
  const controller = new AbortController();
  const init = { method: request.method, headers: request.headers, signal: controller.signal };
  if (request.body != null) init.body = request.body;

  fetch(request.url, init)
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
      }
      if (!response.body) throw new Error('No response body');
      handlers.onOpen({
        status: response.status,
        statusText: response.statusText || 'OK',
        contentType: response.headers.get('content-type') || '',
      });
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        handlers.onText(value);
      }
      handlers.onEnd();
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') handlers.onError(error);
    });

  return () => controller.abort();
}

/**
 * @param {{ method: string, url: string, headers: object, body?: string }} request
 * @param {{ onOpen: Function, onText: Function, onEnd: Function, onError: Function }} handlers
 */
export function openSseStream(request, handlers) {
  return isTauri() ? openNative(request, handlers) : openFetch(request, handlers);
}
